<template>
  <div>
    <SelectorPanel
      type="powerOutage"
      text="Power Outage"
      :disabled="disabled"
      :force-close="forceClose"
      @click="handlePanelOpen"
      content-class="px-0"
    >
      <v-container class="pa-0 hyp-search-container hyp-search-container-min">
        <div class="pull-left hyp-search-filter-container px-2">
          <SelectorPanelHeader
            type="powerOutage"
            title="Power Outage"
            class="pb-2"
            @click-close="handleClickClose"
          >
          </SelectorPanelHeader>
          <v-row class="pt-3 justify-center">United States Only</v-row>
          <v-row class="pt-3 justify-center">
            <SelectorButton
              :title="title"
              type="powerOutage"
              class=""
              @click="handleChangePowerOutageEnabled"
            ></SelectorButton>
          </v-row>
          <br />
          <br />
          <v-row class="pt-3 justify-left legend-spacing">
            <div class="box-normal"></div>
            <span class="text-wrap">Normal: no anomalies in last 6 hours</span>
          </v-row>
          <v-row class="pt-3 justify-left legend-spacing">
            <div class="box-fluctuation"></div>
            <span class="text-wrap"
              >Fluctuating: 1-5 consecutive anomalies in last 6 hours</span
            >
          </v-row>
          <v-row class="pt-3 justify-left legend-spacing">
            <div class="box-outage"></div>
            <span class="text-wrap"
              >Impaired: 6 consecutive anomalies in last 6 hours</span
            >
          </v-row>
        </div>
      </v-container>
    </SelectorPanel>
  </div>
</template>

<script>
import lodashDebounce from "lodash/debounce";

import {
  MODULE_PREFIX_GRIDS,
  MODULE_PREFIX_HOME,
  MODULE_PREFIX_MAP,
  MODULE_PREFIX_PLACES,
  MODULE_PREFIX_POWER_OUTAGE,
} from "@/store";
import {
  GET_DETAILS_SIDEBAR,
  GET_PLACES_REFRESH_MAP,
  GET_POWER_OUTAGE_REFRESH_MAP,
  GET_STABILITY_REFRESH_MAP,
  GET_STABILITY_PARAMS,
  SAVE_PREFERENCES,
} from "@/store/actions.type";
import { removeMapData, mapPolygonData } from "@/common/maps/features";
import { MAIN_MAP_ID } from "@/common/constants/maps";
import { getDefaultMarkerOptions } from "@/common/maps/styles";
import { EVENT_ACTIONS, EVENT_CATEGORIES } from "@/common/constants/analytics";
import { mapGetters } from "vuex";
import { pageTrackerLogger } from "@/common/pageTracker";
import { DETAIL_SIDEBAR_TYPES } from "@/common/constants/home";
import { GET_COUNTY_DETAILS } from "@/store/actions.type";
import {
  getPreference,
  savePreference,
} from "@/services/powerOutage/powerOutage.service";
import { POWER_OUTAGE } from "@/common/config";

const EMITTED_ACTIONS = {
  CHANGE_POWER_OUTAGE_ENABLED: "change-power-outage-enabled",
};

export default {
  name: "PowerOutageSelector",
  components: {
    SelectorPanel: () => import("@/components/common/SelectorPanel"),
    SelectorPanelHeader: () =>
      import("@/components/common/SelectorPanelHeader"),
    SelectorButton: () => import("@/components/common/SelectorButton"),
  },
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    isOpen: false,
    forceClose: null,
    isMinimized: false,
    refresh: new Date().toISOString(),
    powerOutageEnabled: false,
    // Need to reference by value in a callback so making an object (perhaps there's a better way??)
    isClicked: {
      value: false,
    },
  }),
  mounted() {
    this.getPrefs();
  },
  computed: {
    title() {
      return this.powerOutageEnabled ? "Enabled" : "Disabled";
    },
    ...mapGetters({
      selectorPanel: MODULE_PREFIX_HOME + "selectorPanel",
      maps: MODULE_PREFIX_MAP + "maps",
      detailsSidebar: MODULE_PREFIX_HOME + "detailsSidebar",
      refreshMap: MODULE_PREFIX_POWER_OUTAGE + "refreshMap",
    }),
    map() {
      return this.maps[MAIN_MAP_ID];
    },
  },
  methods: {
    getPrefs() {
      // Get saved User Preferences for if Power Outage is enabled
      this.powerOutageEnabled =
        getPreference(this.$store, true, "enabled", "powerOutage") ??
        this.powerOutageEnabled;
      this.selectorPanel["powerOutage"] = this.powerOutageEnabled;
      this.$emit(
        EMITTED_ACTIONS.CHANGE_POWER_OUTAGE_ENABLED,
        this.powerOutageEnabled
      );
    },
    handlePanelOpen(open) {
      lodashDebounce(() => {
        this.isOpen = open;
      }, 1000)();
    },
    handleClickClose() {
      this.forceClose = new Date().toISOString();
    },
    handleClickMinimize() {
      this.isMinimized = !this.isMinimized;
    },
    handleChangePowerOutageEnabled() {
      this.powerOutageEnabled = !this.powerOutageEnabled;
      this.selectorPanel["powerOutage"] = this.powerOutageEnabled;
      this.isClicked.value = this.powerOutageEnabled;

      if (this.powerOutageEnabled) {
        this.addToMap("powerOutage");

        // Approx bounds for lower 48 states; [lng, lat] format
        // Not sure where a value like this should actually be stored
        const bbox = [
          [-147.9083, 20.2446],
          [-44.832, 52.9463],
        ];
        this.map.fitBounds(bbox, {});
        this.removeStabilityLayer();
      } else {
        this.removeFromMap("powerOutage");
      }
      savePreference(
        this.$store,
        this.powerOutageEnabled,
        "enabled",
        "powerOutage"
      );
      this.$emit(
        EMITTED_ACTIONS.CHANGE_POWER_OUTAGE_ENABLED,
        this.powerOutageEnabled
      );
      if (this.powerOutageEnabled) {
        this.handlePageTrackerEvent(
          EVENT_CATEGORIES.powerOutage,
          EVENT_ACTIONS.clicked,
          "Power Outage Enabled"
        );
      }
    },
    removeStabilityLayer() {
      this.$store.dispatch(MODULE_PREFIX_GRIDS + GET_STABILITY_PARAMS, {
        type: null,
        levels:
          this.preferences &&
          this.preferences.grids &&
          this.preferences.grids.stability &&
          this.preferences.grids.stability.levels
            ? this.preferences.grids.stability.levels
            : [1, 2, 3, 4, 5],
        opacity:
          this.preferences &&
          this.preferences.grids &&
          this.preferences.grids.stability &&
          this.preferences.grids.stability.opacity
            ? this.preferences.grids.stability.opacity
            : 50,
      });
      this.$store.dispatch(
        MODULE_PREFIX_GRIDS + GET_STABILITY_REFRESH_MAP,
        true
      );
      savePreference(this.$store, null, "stability.type", "grids");
    },
    handlePageTrackerEvent(category, action, name) {
      let _paq = (window._paq = window._paq || []);
      _paq.push(["trackEvent", category, action, name]);
      pageTrackerLogger(
        "Page Tracker Event " +
          category +
          " " +
          action +
          " " +
          name +
          " logged."
      );
    },
    addToMap(type) {
      const LAYER_ID = type ? `layer-${type}` : "layer-powerOutage";
      this.removeFromMap(LAYER_ID);

      const PARAMS = {
        id: LAYER_ID,
        noCluster: false,
      };

      const layerOptions = {
        polygonLayerOptions: {
          id: LAYER_ID,
          type: "fill",
          source: LAYER_ID,
          paint: {
            "fill-color": [
              "case",
              ["==", ["get", "c"], "red"],
              "#f8887a",
              ["==", ["get", "c"], "yellow"],
              "#f4f63d",
              "#7e7f80",
            ],
            "fill-opacity": 0.4,
          },
        },
      };

      const OPTIONS = getDefaultMarkerOptions(PARAMS);
      /*
       * OPTIONS
       * id:  source ID
       * data: endpoint for markers
       * mapClickEvent: function when markers are clicked
       * polygonOptions:  options for polygons
       * map:  map object
       * */
      mapPolygonData({
        id: LAYER_ID,
        data: `${POWER_OUTAGE}/countyMap_outage.json`,
        mapClickEvent: this.mapClickEvent,
        markerOptions: OPTIONS,
        map: this.map,
        polygonOptions: layerOptions,
      });

      this.handlePageTrackerEvent(
        EVENT_CATEGORIES.powerOutage,
        EVENT_ACTIONS.addDataToMap,
        `Added To Map ---- ${type}`
      );
    },
    async mapClickEvent(e, features) {
      e.originalEvent.stopPropagation();

      if (features.length > 0) {
        setTimeout(async () => {
          this.detailsSidebar.visible = true;

          this.detailsSidebar.selected = DETAIL_SIDEBAR_TYPES.powerOutage;
          this.detailsSidebar.type = DETAIL_SIDEBAR_TYPES.powerOutage;
          const NAME = `layer-${features[0].properties.t}`;

          const PARAMS = {
            title: NAME,
            feature: features[0],
          };
          this.$store.dispatch(
            MODULE_PREFIX_POWER_OUTAGE + GET_COUNTY_DETAILS,
            PARAMS
          );

          await this.$store.dispatch(
            MODULE_PREFIX_HOME + GET_DETAILS_SIDEBAR,
            this.detailsSidebar
          );

          this.handlePageTrackerEvent(
            EVENT_CATEGORIES.powerOutage,
            EVENT_ACTIONS.clicked,
            `County Click --- ${features[0].properties.i}`
          );
        }, 300);
      }
    },
    removeFromMap(type) {
      if (this.map) {
        const LAYER_ID = type ? `layer-${type}` : "layer-powerOutage";
        removeMapData(this.map, LAYER_ID);
      }
    },
  },
  watch: {
    isOpen() {
      if (this.isOpen) {
        this.refresh = new Date().toISOString();
      }
    },
    refreshMap: {
      async handler() {
        if (this.refreshMap && this.powerOutageEnabled) {
          this.addToMap("powerOutage");
          this.$store.dispatch(
            MODULE_PREFIX_POWER_OUTAGE + GET_POWER_OUTAGE_REFRESH_MAP,
            false
          );
        }
      },
    },
  },
  beforeDestroy() {
    this.isOpen = false;
  },
};
</script>

<style scoped>
.hyp-search-container {
  padding: 0 !important;
  display: grid;
  grid-template-columns: 323px auto;
}
.hyp-search-container-min {
  width: 332px;
}
.hyp-search-filter-container {
  box-sizing: border-box;
  overflow-y: auto;
  overflow-x: hidden;
  height: 100vh;
}
.box-normal {
  border: 1px solid #555353;
  background: #7e7f80;
  height: 30px;
  width: 30px;
}
.box-fluctuation {
  border: 1px solid #b8b92d;
  background: #f4f63d;
  height: 30px;
  width: 30px;
}
.box-outage {
  border: 1px solid #ba655b;
  background: #f8887a;
  height: 30px;
  width: 30px;
}
[class*="box-"] {
  border-radius: 3px;
}
.text-wrap {
  inline-size: 250px;
  overflow-wrap: break-word;
  padding-left: 0.5rem;
  margin-top: -0.5rem;
}
.legend-spacing {
  padding-left: 2rem;
  margin-top: 0.5rem;
}
</style>
